import type { AuthProvider, AuthSession, NativeOAuthHandlingResult, SignInOptions } from '../auth/authProvider'
import { createNativeOAuthAttempt, parseNativeOAuthCallback } from '../auth/nativeOAuth'
import type { NativeOAuthAttempt } from '../supabase/authStorage'
import type { AppEnvironment } from '../supabase/config'
import { NetworkError, type AuthApi } from './authApi'
import { challengeFor, createVerifier } from './pkce'
import { toAuthSession, type StoredNeonSession } from './session'
import type { TokenManager } from './tokenManager'

export interface SessionStore {
  read(): Promise<StoredNeonSession | null>
  write(session: StoredNeonSession): Promise<void>
  clear(): Promise<void>
}

export interface AttemptStore {
  read(): Promise<NativeOAuthAttempt | null>
  write(attempt: NativeOAuthAttempt): Promise<void>
  clear(expectedId?: string): Promise<boolean>
}

export interface NeonAuthDeps {
  environment: AppEnvironment
  api: AuthApi
  tokens: Pick<TokenManager, 'clear'>
  sessions: SessionStore
  attempts: AttemptStore
  native: {
    isAndroid(): boolean
    openBrowser(url: string): Promise<void>
    closeBrowser(): Promise<void>
  }
  web: {
    origin(): string
    /** pathname + search + hash, as the person sees it. */
    currentPath(): string
    search(): string
    navigate(url: string): void
    replacePath(path: string): void
  }
  now(): number
}

/** Revalidate the stored session with the server at most this often; the app itself never waits for it. */
const REVALIDATE_EVERY_MS = 10 * 60_000
const COMPLETE_FAILED = 'Unable to complete Google sign-in. Reconnect and try again.'

/**
 * Sign-in against the auth Worker (apps/auth). Same contract as the Supabase provider, with two differences that matter for a local-first app:
 *  - getSession() answers from the device and revalidates in the background; offline or a server error never signs anyone out. Only the server
 *    saying "this session is invalid" does.
 *  - The hand-off is bound to the app: a secret verifier stays on the device and is needed to redeem the code that comes back in the link.
 */
export class NeonAuthProvider implements AuthProvider {
  readonly id = 'neon-google'
  private readonly listeners = new Set<(session: AuthSession | null) => void>()
  private lastValidated = 0
  private revalidating: Promise<void> | null = null

  constructor(private readonly deps: NeonAuthDeps) {}

  async initialize(): Promise<void> {}

  async signIn(_options: SignInOptions = {}): Promise<null> {
    // Every sign-in creates a brand-new server session, which is also what a "sign in again" for sensitive actions needs.
    const { deps } = this
    const verifier = createVerifier()
    const challenge = await challengeFor(verifier)
    const android = deps.native.isAndroid()
    const attempt: NativeOAuthAttempt = {
      ...createNativeOAuthAttempt(deps.environment, deps.web.currentPath(), deps.now()),
      verifier,
    }
    await deps.attempts.write(attempt)
    const url = deps.api.startUrl({ attempt: attempt.id, challenge, ...(android ? {} : { to: deps.web.origin() }) })
    if (android) {
      try { await deps.native.openBrowser(url) } catch (error) { await deps.attempts.clear(attempt.id); throw error }
    } else {
      deps.web.navigate(url)
    }
    return null
  }

  async signOut(): Promise<void> {
    const stored = await this.deps.sessions.read()
    await this.deps.attempts.clear()
    // Local sign-out is immediate and certain; ending the server session is best effort (offline sign-out still works on the device).
    if (stored) await this.deps.api.signOut(stored.bearer).catch(() => undefined)
    await this.deps.sessions.clear()
    this.deps.tokens.clear()
    this.emit(null)
  }

  async getSession(): Promise<AuthSession | null> {
    await this.completeWebReturn()
    const stored = await this.deps.sessions.read()
    if (!stored) return null
    if (this.deps.now() - this.lastValidated > REVALIDATE_EVERY_MS) void this.revalidate()
    return toAuthSession(stored)
  }

  onSessionChange(listener: (session: AuthSession | null) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  // ---- Android: the App Link brings the code back to the app ------------------------------------------------------------

  async handleNativeOAuthCallback(rawUrl: string): Promise<NativeOAuthHandlingResult> {
    if (!this.deps.native.isAndroid()) return { status: 'ignored' }
    const attempt = await this.deps.attempts.read()
    if (!attempt) return { status: 'ignored' }
    const callback = parseNativeOAuthCallback(rawUrl, this.deps.environment, attempt, this.deps.now())
    if (callback.kind === 'invalid') {
      if (callback.reason === 'expired') return this.cancel(attempt.id)
      return { status: 'ignored' }
    }
    if (callback.kind === 'provider-error') return this.cancel(attempt.id)
    await this.deps.attempts.write({ ...attempt, authorizationCode: callback.code })
    return this.redeemAndStore(attempt.id, callback.code, attempt.verifier ?? null)
  }

  async resumeNativeOAuthCallback(): Promise<NativeOAuthHandlingResult> {
    if (!this.deps.native.isAndroid()) return { status: 'ignored' }
    const attempt = await this.deps.attempts.read()
    if (!attempt) return { status: 'ignored' }
    if (this.deps.now() > attempt.expiresAt) return this.cancel(attempt.id)
    if (!attempt.authorizationCode) return { status: 'ignored' }
    const existing = await this.deps.sessions.read()
    if (existing) {
      await this.deps.attempts.clear(attempt.id)
      await this.closeBrowser()
      return { status: 'success', session: toAuthSession(existing) }
    }
    return this.redeemAndStore(attempt.id, attempt.authorizationCode, attempt.verifier ?? null)
  }

  async cancelPendingNativeOAuth(): Promise<boolean> {
    if (!this.deps.native.isAndroid()) return false
    const attempt = await this.deps.attempts.read()
    if (!attempt || attempt.authorizationCode) return false
    return this.deps.attempts.clear(attempt.id)
  }

  // ---- web: the browser comes back to the app with ?attempt=…&code=… --------------------------------------------------

  private async completeWebReturn(): Promise<void> {
    if (this.deps.native.isAndroid()) return
    const params = new URLSearchParams(this.deps.web.search())
    const attemptId = params.get('attempt')
    const code = params.get('code')
    if (!attemptId || !code) return
    const attempt = await this.deps.attempts.read()
    if (!attempt || attempt.id !== attemptId) return // not ours: leave the address alone
    const leave = () => this.deps.web.replacePath(attempt.returnPath)
    if (this.deps.now() > attempt.expiresAt) { await this.deps.attempts.clear(attempt.id); leave(); return }
    try {
      await this.redeemAndStore(attempt.id, code, attempt.verifier ?? null)
    } catch {
      // A failed sign-in must not block opening the app: the person simply is not signed in and can try again.
    }
    leave()
  }

  // ---- shared ------------------------------------------------------------------------------------------------------------

  private async redeemAndStore(attemptId: string, code: string, verifier: string | null): Promise<NativeOAuthHandlingResult> {
    if (!verifier) {
      await this.deps.attempts.clear(attemptId)
      throw new Error(COMPLETE_FAILED)
    }
    let redeemed
    try { redeemed = await this.deps.api.redeem({ attempt: attemptId, code, verifier }) } catch (error) {
      if (error instanceof NetworkError) throw new Error(COMPLETE_FAILED) // the code is kept, so resume can try again
      throw error
    }
    // The code is single use: whatever happens next, this attempt cannot be replayed.
    await this.deps.attempts.clear(attemptId)
    if (redeemed.status === 'rejected') { await this.closeBrowser(); throw new Error(COMPLETE_FAILED) }
    const lookup = await this.deps.api.getSession(redeemed.bearer).catch(() => null)
    if (!lookup || lookup.status !== 'ok') { await this.closeBrowser(); throw new Error(COMPLETE_FAILED) }
    const stored: StoredNeonSession = { v: 1, bearer: redeemed.bearer, user: lookup.user, expiresAt: lookup.expiresAt }
    await this.deps.sessions.write(stored)
    this.lastValidated = this.deps.now()
    await this.closeBrowser()
    const session = toAuthSession(stored)
    this.emit(session)
    return { status: 'success', session }
  }

  private async revalidate(): Promise<void> {
    this.revalidating ??= (async () => {
      const stored = await this.deps.sessions.read()
      if (!stored) return
      try {
        const lookup = await this.deps.api.getSession(stored.bearer)
        this.lastValidated = this.deps.now()
        if (lookup.status === 'invalid') {
          await this.deps.sessions.clear()
          this.deps.tokens.clear()
          this.emit(null)
          return
        }
        if (lookup.user.id !== stored.user.id) return // never swap identity silently; sign-out and sign-in own that
        await this.deps.sessions.write({ ...stored, user: lookup.user, expiresAt: lookup.expiresAt })
      } catch { /* offline or server trouble: keep what is on the device */ }
    })().finally(() => { this.revalidating = null })
    await this.revalidating
  }

  /** Called by the token manager when the server says the session is gone. */
  async sessionInvalidated(): Promise<void> {
    await this.deps.sessions.clear()
    this.deps.tokens.clear()
    this.emit(null)
  }

  private async cancel(attemptId: string): Promise<NativeOAuthHandlingResult> {
    await this.deps.attempts.clear(attemptId)
    await this.closeBrowser()
    return { status: 'canceled' }
  }

  private async closeBrowser(): Promise<void> {
    await this.deps.native.closeBrowser().catch(() => undefined)
  }

  private emit(session: AuthSession | null): void {
    for (const listener of this.listeners) listener(session)
  }
}
